// @ts-check
// Modelo lógico de "algoritmos" ({ id, nombre, texto }) compartido por los
// editores de TEXTO (pseudocódigo y Python).
//
// ## Por qué existe este archivo
//
// `features/pseudocode-editor/pseudo-model.js` (TSK-0092) y
// `features/python-editor/py-model.js` (TSK-0096) eran el MISMO archivo:
// mismas constantes, mismas siete funciones, mismos mensajes de error carácter
// a carácter. Cada uno justificaba la copia con "componentes de features
// distintas no se importan entre sí" (docs/03-frontend.md §4.2). La regla es
// correcta, pero la conclusión no: lo que prohíbe §4.2 es que una feature
// importe archivos INTERNOS de otra feature, no que ambas compartan un módulo
// de una capa inferior. `state` es exactamente esa capa ("modelos/funciones
// puras", §4.2/§4.3), y `features → state` es una dirección permitida.
//
// El molde ya lo usa el proyecto: `state/attempt-persistence.js` con su
// adaptador de 16 líneas en `features/diagram-editor/attempt-persistence.js`.
// Aquí se repite: este módulo tiene la lógica; cada feature conserva su archivo
// con su API pública intacta, reexportando desde aquí.
//
// ## Qué es y qué NO es (docs/03-frontend.md §4.3, §8.5)
//
// Dato + funciones puras: sin DOM, sin `fetch`, sin dependencia de sesión. Un
// "algoritmo" es `{ id, nombre, texto }` -- TEXTO PLANO, no un árbol de
// bloques. `features/diagram-editor/editor-model.js` (zona protegida) resuelve
// un problema distinto (nodos IR con hijos) y NO se toca ni se importa; se leyó
// en su día como referencia de patrón (mismo idioma de inmutabilidad
// `{ ...x, campo: nuevoValor }`, mismo estilo de validación con errores
// explícitos) y esa herencia se conserva.
//
// Diferencia deliberada de invariante frente a `editor-model.js`: allí
// "Principal" NUNCA es un elemento de la lista gestionada (`programa.funciones`
// solo contiene funciones reales; el programa principal es `programa.cuerpo`,
// un campo aparte), así que la protección vive en la UI. Aquí la lista SIEMPRE
// empieza por un algoritmo fijo `'principal'` no eliminable DENTRO de esa misma
// lista, así que la invariante se hace cumplir en el modelo: ningún consumidor
// puede romperla por error.

/** Id fijo y reservado del algoritmo principal. Nunca lo genera
 * `crearAlgoritmo` (que siempre genera un id nuevo para una función), y
 * `eliminarAlgoritmo` rechaza explícitamente cualquier intento de
 * eliminarlo. */
export const ID_PRINCIPAL = 'principal';

/** Nombre visible fijo del algoritmo principal (mismo texto que la pestaña
 * "Principal" de `editor-controller.js`). */
const NOMBRE_PRINCIPAL = 'Principal';

// Contador de módulo. Al unificar los dos modelos pasa a ser GLOBAL entre
// editores en vez de uno por editor. Es seguro y no cambia nada observable:
//
//   1. La aplicación es una MPA (docs/03-frontend.md §4). `pseudocode.html` y
//      `python.html` son documentos distintos: nunca se carga el editor de
//      pseudocódigo y el de Python en la misma página, así que en ejecución
//      real el contador se comporta exactamente igual que antes.
//   2. Ningún consumidor compara ids ENTRE features. El único módulo que toca
//      los dos modelos, `features/translation/entrega.js`, solo usa
//      `algoritmos[0].id` (que es `ID_PRINCIPAL`, una constante, no un id
//      generado). Verificado con `grep` sobre `src/` y `tests/`: no hay ningún
//      id `algoritmo-N` literal en ninguna parte.
//
// Limitación PREEXISTENTE que esta unificación ni introduce ni arregla: el
// contador arranca en 0 en cada carga de página, mientras que los ids
// restaurados del autoguardado vienen de la sesión anterior. Crear funciones
// tras restaurar puede reutilizar un id ya presente. Ya pasaba con los dos
// modelos separados y se deja como está (arreglarlo cambiaría el
// comportamiento, que es justo lo que esta refactorización no debe hacer).
let contadorId = 0;

/**
 * Genera un id de algoritmo único y estable dentro de la sesión del editor.
 * Nunca coincide con `ID_PRINCIPAL` (prefijo distinto).
 * @returns {string}
 */
function generarId() {
  contadorId += 1;
  return `algoritmo-${contadorId}`;
}

/**
 * @param {unknown} valor
 * @param {string} nombreCampo
 * @returns {string}
 */
function requerirCadena(valor, nombreCampo) {
  if (typeof valor !== 'string' || valor.length === 0) {
    throw new TypeError(`${nombreCampo} debe ser una cadena no vacía`);
  }
  return valor;
}

/**
 * @typedef {{ id: string, nombre: string, texto: string }} Algoritmo
 */

/**
 * @param {unknown} valor
 * @returns {valor is Algoritmo}
 */
export function esAlgoritmo(valor) {
  if (typeof valor !== 'object' || valor === null) return false;
  const candidato = /** @type {Record<string, unknown>} */ (valor);
  return (
    typeof candidato.id === 'string' &&
    typeof candidato.nombre === 'string' &&
    typeof candidato.texto === 'string'
  );
}

/**
 * Lista inicial de algoritmos: SIEMPRE un único elemento, el algoritmo
 * principal fijo (`id: ID_PRINCIPAL`, `nombre: 'Principal'`), con `texto`
 * vacío salvo que se indique lo contrario (p.ej. al restaurar un guardado
 * previo, o al dejar una traducción en el editor de destino, DEC-107).
 * @param {{ textoPrincipal?: string }} [opciones]
 * @returns {Algoritmo[]}
 */
export function crearListaAlgoritmos({ textoPrincipal = '' } = {}) {
  if (typeof textoPrincipal !== 'string') {
    throw new TypeError('textoPrincipal debe ser una cadena');
  }
  return [{ id: ID_PRINCIPAL, nombre: NOMBRE_PRINCIPAL, texto: textoPrincipal }];
}

/**
 * Crea un algoritmo NUEVO (una función), con un `id` generado que nunca es
 * `ID_PRINCIPAL`. No valida aquí formato de identificador ni nombres
 * duplicados: esa es una decisión de UI (ver las pestañas de función de los
 * editores, que sí aplican esas dos validaciones antes de llamar a esta
 * factoría).
 * @param {{ nombre: string, texto?: string }} campos
 * @returns {Algoritmo}
 */
export function crearAlgoritmo({ nombre, texto = '' }) {
  requerirCadena(nombre, 'nombre');
  if (nombre === NOMBRE_PRINCIPAL) {
    throw new TypeError(`nombre no puede ser "${NOMBRE_PRINCIPAL}": ese nombre está reservado al algoritmo principal`);
  }
  if (typeof texto !== 'string') {
    throw new TypeError('texto debe ser una cadena');
  }
  return { id: generarId(), nombre, texto };
}

/**
 * Añade `algoritmo` de forma inmutable al final de `lista`
 * (`[...lista, algoritmo]`). Rechaza explícitamente cualquier intento de
 * añadir un segundo algoritmo con `id === ID_PRINCIPAL` (esta lista solo
 * puede tener UNO fijo, el que ya pone `crearListaAlgoritmos`).
 * @param {Algoritmo[]} lista
 * @param {Algoritmo} algoritmo
 * @returns {Algoritmo[]}
 */
export function agregarAlgoritmo(lista, algoritmo) {
  if (!Array.isArray(lista)) {
    throw new TypeError('agregarAlgoritmo requiere una lista de algoritmos');
  }
  if (!esAlgoritmo(algoritmo)) {
    throw new TypeError('algoritmo debe tener { id, nombre, texto }');
  }
  if (algoritmo.id === ID_PRINCIPAL) {
    throw new TypeError(`no se puede agregar otro algoritmo con id "${ID_PRINCIPAL}"`);
  }
  return [...lista, algoritmo];
}

/**
 * Elimina de forma inmutable el algoritmo cuyo `id` es `id` de `lista`
 * (`.filter(...)` sobre una copia). Lanza `RangeError` si
 * `id === ID_PRINCIPAL` (criterio de aceptación: "'principal' siempre existe
 * y no es eliminable"). Si `id` no existe en `lista` (y no es
 * `ID_PRINCIPAL`), devuelve `lista` sin cambios (misma referencia).
 * @param {Algoritmo[]} lista
 * @param {string} id
 * @returns {Algoritmo[]}
 */
export function eliminarAlgoritmo(lista, id) {
  if (!Array.isArray(lista)) {
    throw new TypeError('eliminarAlgoritmo requiere una lista de algoritmos');
  }
  if (id === ID_PRINCIPAL) {
    throw new RangeError('el algoritmo principal no se puede eliminar');
  }
  if (!lista.some((algoritmo) => algoritmo.id === id)) return lista;
  return lista.filter((algoritmo) => algoritmo.id !== id);
}

/**
 * Reemplaza de forma inmutable el `texto` del algoritmo cuyo `id` es `id`
 * dentro de `lista` (`.map(...)` sobre una copia, sin mutar ningún elemento
 * original). Funciona igual para `ID_PRINCIPAL` que para cualquier otro id
 * (el texto del algoritmo principal SÍ se puede editar; solo ELIMINARLO
 * está prohibido). Si no existe ningún algoritmo con ese `id`, devuelve
 * `lista` sin cambios (misma referencia).
 * @param {Algoritmo[]} lista
 * @param {string} id
 * @param {string} texto
 * @returns {Algoritmo[]}
 */
export function actualizarTextoAlgoritmo(lista, id, texto) {
  if (!Array.isArray(lista)) {
    throw new TypeError('actualizarTextoAlgoritmo requiere una lista de algoritmos');
  }
  if (typeof texto !== 'string') {
    throw new TypeError('texto debe ser una cadena');
  }
  let huboCambio = false;
  const nuevaLista = lista.map((algoritmo) => {
    if (algoritmo.id !== id) return algoritmo;
    huboCambio = true;
    return { ...algoritmo, texto };
  });
  if (!huboCambio) return lista;
  return nuevaLista;
}

/**
 * @param {Algoritmo[]} lista
 * @param {string} id
 * @returns {Algoritmo | undefined}
 */
export function obtenerAlgoritmoPorId(lista, id) {
  return lista.find((algoritmo) => algoritmo.id === id);
}
